"use client";
import React, { useState } from "react";
import Image from "next/image";
import SuitabilityTestModal from "../modal/SuitabilityTestModal";
import { usePathname } from "next/navigation";
import { useIsMobile } from "@/utils/useIsMobile";
import { ROOT_URL } from "@/utils/gloableVariables";

type StripName = {
  name: string;
  imgCode: string;
  schemeCode: string;
  schemeCatId: string;
  isin: string;
};

export default function SuitabilityTestStrip({
  name,
  imgCode,
  schemeCode,
  schemeCatId,
  isin,
}: StripName) {
  const [isOepn, setIsOpen] = useState(false);

  const pathname = usePathname();
  const isMobile = useIsMobile(1220);

  if (isMobile && pathname.includes("portfolio-details")) return;

  const thumbUp = (
    <svg
      width="60"
      height="60"
      viewBox="0 0 60 60"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-10 h-10 xs:w-15 xs:h-15"
    >
      <g filter="url(#filter0_d_8_2)">
        <rect
          x="8"
          y="7"
          width="44"
          height="44"
          rx="22"
          fill="white"
          fillOpacity="0.26"
          shapeRendering="crispEdges"
        />
        <rect
          x="8.28605"
          y="7.28605"
          width="43.4279"
          height="43.4279"
          rx="21.714"
          stroke="white"
          strokeOpacity="0.38"
          strokeWidth="0.572095"
          shapeRendering="crispEdges"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M33.9212 26.0023H39.0524C39.4691 26.0023 39.8806 26.1058 40.2494 26.3025C40.6183 26.4992 40.9379 26.7865 41.1773 27.1372C41.4163 27.4907 41.5691 27.8952 41.6235 28.3184C41.6778 28.7416 41.6323 29.1716 41.4905 29.574L38.4364 38.893C38.0637 39.9464 37.8152 40.6465 35.9983 40.6465C33.3168 40.6465 30.4646 39.7704 28.0304 39.0211C27.4196 38.8334 26.8346 38.6536 26.2859 38.4983H26.1371V25.7357C28.2825 23.6187 30.1278 21.2178 31.6216 18.5999C31.8727 18.1166 32.2853 17.7365 32.7876 17.5258C33.2852 17.3183 33.8409 17.2962 34.3534 17.4637C34.8659 17.6319 35.3059 17.9774 35.5958 18.4433C35.887 18.9092 36.0099 19.4631 35.9439 20.0105L33.9212 26.0023ZM19.8761 26.4112H23.5294V36.7642C23.5294 37.4506 23.2567 38.1089 22.7714 38.5943C22.286 39.0797 21.6276 39.3524 20.9412 39.3524C20.2548 39.3524 19.5964 39.0797 19.111 38.5943C18.6257 38.1089 18.353 37.4506 18.353 36.7642V27.9344C18.353 27.0932 19.0363 26.4112 19.8761 26.4112Z"
          fill="#2FFF1C"
        />
      </g>
      <defs>
        <filter
          id="filter0_d_8_2"
          x="0.333931"
          y="-0.666069"
          width="59.3321"
          height="59.9225"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="0.750588" />
          <feGaussianBlur stdDeviation="3.75294" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.25 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow_8_2"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_8_2"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  );

  const thumbDown = (
    <svg
      width="60"
      height="60"
      viewBox="0 0 60 60"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-10 h-10 xs:w-15 xs:h-15"
    >
      <g filter="url(#filter0_d_6_16)">
        <rect
          x="8"
          y="7"
          width="44"
          height="44"
          rx="22"
          fill="white"
          fillOpacity="0.26"
          shapeRendering="crispEdges"
        />
        <rect
          x="8.28605"
          y="7.28605"
          width="43.4279"
          height="43.4279"
          rx="21.714"
          stroke="white"
          strokeOpacity="0.38"
          strokeWidth="0.572095"
          shapeRendering="crispEdges"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M33.9212 31.9858H39.0524C39.4691 31.9858 39.8806 31.8822 40.2494 31.6855C40.6183 31.4888 40.9379 31.2015 41.1773 30.8508C41.4163 30.4974 41.5691 30.0928 41.6235 29.6696C41.6778 29.2464 41.6323 28.8164 41.4905 28.414L38.4364 19.0951C38.0637 18.0416 37.8152 17.3415 35.9983 17.3415C33.3168 17.3415 30.4646 18.2177 28.0304 18.9669C27.4196 19.1546 26.8346 19.3345 26.2859 19.4898H26.1371V32.2524C28.2825 34.3693 30.1278 36.7702 31.6216 39.3881C31.8727 39.8715 32.2853 40.2515 32.7876 40.4622C33.2852 40.6698 33.8409 40.6918 34.3534 40.5244C34.8659 40.3561 35.3059 40.0106 35.5958 39.5447C35.887 39.0788 36.0099 38.5249 35.9439 37.9775L33.9212 31.9858ZM19.8761 31.5768H23.5294V21.2239C23.5294 20.5374 23.2567 19.8791 22.7714 19.3937C22.286 18.9083 21.6276 18.6357 20.9412 18.6357C20.2548 18.6357 19.5964 18.9083 19.111 19.3937C18.6257 19.8791 18.353 20.5374 18.353 21.2239V30.0536C18.353 30.8948 19.0363 31.5768 19.8761 31.5768Z"
          fill="#FF881D"
        />
      </g>
      <defs>
        <filter
          id="filter0_d_6_16"
          x="0.333931"
          y="-0.666069"
          width="59.3321"
          height="59.9225"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="0.750588" />
          <feGaussianBlur stdDeviation="3.75294" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.25 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow_6_16"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_6_16"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  );

  return (
    <div
      className={`xl:sticky ${isOepn ? "xl:z-5700" : "xl:z-4"} top-40 w-full max-w-91 mx-auto rounded-4xl bg-white p-5 xs:p-6 shrink-0 text-center border border-[#1A51AA80]`}
    >
      <div className="w-71 xs:w-79 mx-auto h-57.5 shrink-0 relative">
        <p
          className="absolute top-5 left-0 right-0 z-1 font-semibold text-sm xs:text-base text-white text-center px-6 xs:px-8 line-clamp-2"
          title={name}
        >
          {name}
        </p>
        <Image
          src={`${ROOT_URL}img/AMCsLogo/${imgCode}.png`}
          alt="Suitabilty Test For Finding this Fund is Suitable or Not for User."
          priority
          width={56}
          height={56}
          className="object-contain overflow-hidden w-full h-full max-w-12 max-h-12 xs:max-w-14 xs:max-h-14 rounded-full border-2 border-[#FFCA28] absolute z-1 top-0 bottom-0 left-0 right-0 m-auto"
        />
        <Image
          src="/next_assets/images/strips/suitablity-test-strip.png"
          alt="Suitabilty Test For Finding this Fund is Suitable or Not for User."
          priority
          width={316}
          height={230}
          className="object-contain shrink-0 w-full rounded-5xl shadow-[0px_4px_9px_0px_#0000001A] relative z-0"
        />
        <span
          className="absolute bottom-10 left-6 z-1 animate-float"
          style={{ animationDelay: "0.5s" }}
        >
          {thumbUp}
        </span>
        <span
          className="absolute bottom-10 right-6 z-1 animate-float"
          style={{ animationDelay: "1.2s" }}
        >
          {thumbDown}
        </span>
      </div>

      <div className="text-lg xs:text-xl xs:leading-7 font-bold leading-6 tracking-[-0.4px] mt-3">
        Is This Mutual Fund Right for You?
      </div>

      <p className="text-sm font-normal text-[#202020CC] mt-2">
        <strong className="font-bold text-[#008C54]">+10k</strong> investors
        used our{" "}
        <strong className="font-bold text-[#008C54]">Suitability Test</strong>{" "}
        to get better returns
      </p>

      <SuitabilityTestModal
        schemeCatId={schemeCatId}
        schemeCode={schemeCode}
        name={name}
        imgCode={imgCode}
        isin={isin}
        onShowModalChange={(isOpenModal) => {
          setIsOpen(isOpenModal);
        }}
      />
    </div>
  );
}
